import type { Character } from "../types/entity.js";

/**
 * B05 Reaction system — enabled-list + trigger matcher.
 *
 * This module owns the *registry* and the *matcher*. Actual reaction
 * effects (Shield's +5 AC, Parry's damage reduction, Counterspell, etc.)
 * are implemented by downstream tasks — S10 wires spell reactions, and
 * Tier-5 tasks add the physical ones. For v1 we ship the infrastructure
 * and an event emission so UIs can render "Parry! Shield!" badges.
 *
 * A reaction is:
 *   - identified by a short slug (e.g. "shield", "parry")
 *   - gated by a **trigger** — the combat moment at which it fires
 *   - optionally restricted to a character class (e.g. Shield is wizard)
 *
 * A character enables reactions by adding their IDs to `character.reactions`.
 * On every combat event the engine calls `matchReactions(character, trigger)`
 * with the defender (or attacker, for on_crit-style triggers) and the
 * trigger kind; the returned IDs are the reactions that should fire.
 */

export type ReactionTrigger =
  /** Defender was hit by any attack. */
  | "on_hit_taken"
  /** Defender was *missed* (for abilities like Uncanny Dodge rebuttals). */
  | "on_miss_taken"
  /** Defender took damage that would drop them to 0 HP. */
  | "on_fatal_damage"
  /** Attacker rolled a natural 20 against the defender. */
  | "on_crit_taken"
  /** An enemy began casting a spell — drives Counterspell (S10/S11). */
  | "on_enemy_cast"
  /** An ally hit 0 HP — drives Healing Word (S10). */
  | "on_ally_fatal_damage";

export interface ReactionDefinition {
  readonly id: string;
  readonly trigger: ReactionTrigger;
  /** If set, only characters with this `characterClass` can use the reaction. */
  readonly requiredClass?: string;
}

const registry = new Map<string, ReactionDefinition>();

export function registerReaction(def: ReactionDefinition): void {
  registry.set(def.id, def);
}

export function getReaction(id: string): ReactionDefinition | null {
  return registry.get(id) ?? null;
}

/** Test-only helper to wipe the registry between specs. */
export function __resetReactionsForTests(): void {
  registry.clear();
}

/**
 * Return the subset of `character.reactions` whose registered definition
 * matches `trigger` and whose class gate (if any) allows this character.
 * Unknown IDs are silently skipped — a character may have been saved with
 * a reaction the adventure no longer ships.
 */
export function matchReactions(
  character: Pick<Character, "reactions" | "characterClass">,
  trigger: ReactionTrigger,
): readonly string[] {
  const matched: string[] = [];
  for (const id of character.reactions) {
    const def = registry.get(id);
    if (!def) continue;
    if (def.trigger !== trigger) continue;
    if (def.requiredClass && def.requiredClass !== character.characterClass) continue;
    matched.push(id);
  }
  return matched;
}
